import { useState, useMemo } from "react";
import { BodyTableUsers } from "./BodyTableUsers";
import styles from "../styles/usersTable.module.scss";
import { IUsersData } from "../slices/usersSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import UIcon from "@/assets/uicons/UIcon";

export interface UsersTableProps {
  users: IUsersData[];
  inacUser: (username: string) => void;
  filter: number;
  onEditUser: (user: IUsersData) => void;
}

const columns = [
  { key: 'username', label: 'Nombre' },
  { key: 'email', label: 'Correo' },
  { key: 'role', label: 'Rol' },
  { key: 'areaname', label: 'Equipo' },
];

export const UsersTable = ({ users, inacUser, filter, onEditUser }: UsersTableProps) => {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [sortBy, setSortBy] = useState<keyof IUsersData>("username");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const roles = useSelector((state: RootState) => state.roles.roles);
  const teams = useSelector((state: RootState) => state.teams.teams);

  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(u =>
        u.username?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s)
      );
    }
    if (roleFilter) {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    if (teamFilter) {
      filtered = filtered.filter(u => u.areaname === teamFilter);
    }
    filtered = filtered.slice().sort((a, b) => {
      let aValue = a[sortBy] ?? '';
      let bValue = b[sortBy] ?? '';
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      if (aValue < bValue) return sortDir === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [users, search, roleFilter, teamFilter, sortBy, sortDir]);

  const handleSort = (col: keyof IUsersData) => {
    if (sortBy === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const getSortIcon = (colKey: keyof IUsersData) => {
    if (sortBy !== colKey) return <UIcon name="chevrons-up-down" size={14} className={styles.sortIconInactive} />;
    if (sortDir === 'asc') return <UIcon name="arrow-up" size={16} className={styles.sortIconActive} />;
    return <UIcon name="arrow-down" size={16} className={styles.sortIconActive} />;
  }

  return (
    <>
      <div className={styles.tableToolbar}>
        <input
          type="text"
          placeholder="Buscar por nombre o correo..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={styles.toolbarSearch}
        />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className={styles.toolbarSelect}>
          <option value="">Todos los roles</option>
          {roles?.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
        </select>
        <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} className={styles.toolbarSelect}>
          <option value="">Todos los equipos</option>
          {teams?.map(t => <option key={t.id} value={t.areaName}>{t.areaName}</option>)}
        </select>
      </div>

      <div className={styles["user-table"]}>
        <div>
          <div className={styles["user-table__header"]}>
            {columns.map(col => (
              <div
                key={col.key}
                className={`${styles["user-table__cell"]} ${styles[`user-table__cell--${col.key}`]}`}
                onClick={() => handleSort(col.key as keyof IUsersData)}
              >
                {col.label}
                {getSortIcon(col.key as keyof IUsersData)}
              </div>
            ))}
            <div className={styles["user-table__cell"] + ' ' + styles["user-table__cell--actions"]}></div>
          </div>
          {filteredUsers?.map((user) => (
            <BodyTableUsers
              key={user.userid}
              user={user}
              inacUser={inacUser}
              onEditUser={onEditUser}
              filter={filter}
            />
          ))}
        </div>
      </div>
    </>
  );
};
